import React, { useEffect, useReducer, useRef, useState } from 'react';
import LoadingIcon from 'mdi-react/LoadingIcon';
import PropTypes from 'prop-types';

// Requests that finish faster than this never show the overlay, which stops
// the screen flashing on quick saves and page loads.
const SHOW_DELAY_MS = 300;

// Pages often render several full-page loaders at once (one per data
// source). Each registers here while loading, and only the one that has been
// loading longest draws the overlay - otherwise the translucent veils stack
// and the page washes out. Inline spinners (with a size) aren't shared.
const activeLoaders = new Set();
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());
let nextId = 0;

const Loading = ({ loading, size }) => {
  const idRef = useRef(null);
  if (idRef.current === null) {
    nextId += 1;
    idRef.current = nextId;
  }
  const [, rerender] = useReducer((count) => count + 1, 0);
  const [delayPassed, setDelayPassed] = useState(false);

  // Declared first so it's cleaned up first on unmount - this loader must
  // stop listening before it deregisters and notifies the others.
  useEffect(() => {
    listeners.add(rerender);
    return () => listeners.delete(rerender);
  }, []);

  useEffect(() => {
    if (size || !loading) {
      return undefined;
    }
    const id = idRef.current;
    activeLoaders.add(id);
    notify();
    return () => {
      activeLoaders.delete(id);
      notify();
    };
  }, [loading, size]);

  useEffect(() => {
    if (!loading) {
      setDelayPassed(false);
      return undefined;
    }
    const timer = setTimeout(() => setDelayPassed(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const isOverlayOwner = activeLoaders.values().next().value === idRef.current;
  const visible = loading && delayPassed && (size || isOverlayOwner);

  return (
    <div>
      {visible && (
        <div
          className={size ? 'loading-icon' : 'loading-full'}
          role="status"
          aria-live="polite"
        >
          <LoadingIcon size={size || '48'} color="#0469dc" />
        </div>
      )}
    </div>
  );
};

Loading.propTypes = {
  loading: PropTypes.bool.isRequired,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Loading.defaultProps = {
  size: undefined,
};

export default Loading;
